import { ANIMATION_CAMERA_SOURCE } from './map-scripts.generated';
// The same bounds and marker/control margins run in the browser and WebViews.
function createRouteAnimationCameraTools() {
    const getRouteAnimationCamera = (coordinates, width, height, controlsTop = 6) => {
        if (!(coordinates === null || coordinates === void 0 ? void 0 : coordinates.length) || !(width > 0) || !(height > 0))
            return null;
        let west = Infinity, east = -Infinity, south = Infinity, north = -Infinity, previousLongitude = null;
        const route = [];
        for (let index = 0; index < coordinates.length; index++) {
            const point = coordinates[index];
            if (!point || !Number.isFinite(point[0]) || !Number.isFinite(point[1]))
                continue;
            let longitude = point[0];
            if (previousLongitude !== null && Math.abs(longitude - previousLongitude) > 180) {
                longitude = previousLongitude + (((longitude - previousLongitude + 180) % 360 + 360) % 360) - 180;
            }
            previousLongitude = longitude;
            route.push([longitude, point[1]]);
            west = Math.min(west, longitude);
            east = Math.max(east, longitude);
            south = Math.min(south, point[1]);
            north = Math.max(north, point[1]);
        }
        if (!Number.isFinite(west))
            return null;
        const horizontal = Math.min(64, width * .2);
        const bottom = Math.min(56, height * .2);
        // Reserve room for the progress bar, native toolbar and the marker itself.
        // Keep positive fitting space even inside a compact travel-card map.
        const top = Math.min(Math.max(72, controlsTop + 60), Math.max(0, height - bottom - Math.min(64, height * .3)));
        return { bounds: [[west, south], [east, north]], padding: { top, right: horizontal, bottom, left: horizontal }, maxZoom: 16, coordinates: route };
    };
    // Work on a detached transform: solving the frame must not emit camera
    // events or briefly move the visible map through intermediate positions.
    // Both pinned Mapbox GL versions expose this transform; keep the standard
    // fitBounds fallback available if a future SDK changes its interface.
    const getRouteAnimationCameraPlacement = (map, frame) => {
        var _a, _b, _c;
        if (typeof ((_a = map.transform) === null || _a === void 0 ? void 0 : _a.clone) !== 'function' || typeof map.cameraForBounds !== 'function')
            return null;
        const bearing = map.getBearing(), pitch = map.getPitch();
        const initial = map.cameraForBounds(frame.bounds, { padding: frame.padding, maxZoom: frame.maxZoom, bearing, pitch });
        if (!initial)
            return null;
        const transform = map.transform.clone();
        if (typeof transform.locationPoint3D !== 'function' || typeof transform.pointLocation !== 'function')
            return null;
        transform.minZoom = -2;
        transform.padding = { top: 0, right: 0, bottom: 0, left: 0 };
        transform.bearing = bearing;
        transform.pitch = pitch;
        transform.center = initial.center;
        transform.zoom = initial.zoom;
        const LngLat = map.getCenter().constructor;
        const globe = ((_b = map.getProjection) === null || _b === void 0 ? void 0 : _b.call(map).name) === 'globe';
        const { bounds, padding } = frame;
        const routeCenter = new LngLat((bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2);
        const samples = [];
        if ((_c = frame.coordinates) === null || _c === void 0 ? void 0 : _c.length) {
            // Fit the path, not the empty corners of its geographic bounding box.
            // Keep all four extrema when reducing a large uploaded track.
            const route = frame.coordinates;
            const indices = new Set([0, route.length - 1]);
            let west = 0, east = 0, south = 0, north = 0;
            for (let i = 0; i < route.length; i++) {
                if (route[i][0] < route[west][0])
                    west = i;
                if (route[i][0] > route[east][0])
                    east = i;
                if (route[i][1] < route[south][1])
                    south = i;
                if (route[i][1] > route[north][1])
                    north = i;
                if (i % Math.max(1, Math.ceil(route.length / 64)) === 0)
                    indices.add(i);
            }
            [west, east, south, north].forEach(index => indices.add(index));
            const ordered = [...indices].sort((a, b) => a - b);
            for (let i = 0; i < ordered.length; i++) {
                const a = route[ordered[i]], b = route[ordered[i + 1]] || a;
                const steps = Math.max(1, Math.min(16, Math.ceil(Math.max(Math.abs(b[0] - a[0]), Math.abs(b[1] - a[1])) / 5)));
                for (let j = 0; j < steps; j++)
                    samples.push(new LngLat(a[0] + (b[0] - a[0]) * j / steps, a[1] + (b[1] - a[1]) * j / steps));
            }
        }
        else {
            for (let x = 0; x <= 4; x++)
                for (let y = 0; y <= 4; y++) {
                    samples.push(new LngLat(bounds[0][0] + (bounds[1][0] - bounds[0][0]) * x / 4, bounds[0][1] + (bounds[1][1] - bounds[0][1]) * y / 4));
                }
        }
        const left = padding.left + 4, right = transform.width - padding.right - 4;
        const top = padding.top + 4, bottom = transform.height - padding.bottom - 4;
        if (right <= left || bottom <= top)
            return null;
        const screenBounds = () => {
            let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
            for (const sample of samples) {
                const position = transform.locationPoint3D(sample);
                minX = Math.min(minX, position.x);
                maxX = Math.max(maxX, position.x);
                minY = Math.min(minY, position.y);
                maxY = Math.max(maxY, position.y);
            }
            return { minX, maxX, minY, maxY, x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
        };
        let lowerZoom = -2, upperZoom = frame.maxZoom;
        let zoom = Math.min(upperZoom, Math.max(lowerZoom, initial.zoom));
        let best = null;
        // cameraForBounds is conservative on the globe. Search in screen space in
        // both directions, so a route that fits with unused room also zooms in.
        for (let iteration = 0; iteration < 20; iteration++) {
            transform.center = routeCenter;
            transform.zoom = zoom;
            if (globe && typeof transform.setProjection === 'function') {
                // Mapbox switches globe/Mercator transforms at zoom 6 on the live map.
                // A detached clone needs the same switch for each candidate zoom.
                if (zoom < 6)
                    transform.setProjection({ name: 'globe' });
                else if (typeof transform.setMercatorFromTransition === 'function')
                    transform.setMercatorFromTransition();
            }
            let fits = false;
            const attempts = globe && zoom < 6 ? 30 : 10;
            for (let centering = 0; centering < attempts; centering++) {
                const { minX, maxX, minY, maxY, x, y } = screenBounds();
                if (![minX, maxX, minY, maxY].every(Number.isFinite))
                    break;
                fits = minX >= left && maxX <= right && minY >= top && maxY <= bottom;
                const offsetX = (minX + maxX - left - right) / 2;
                const offsetY = (minY + maxY - top - bottom) / 2;
                if (Math.abs(offsetX) <= .25 && Math.abs(offsetY) <= .25)
                    break;
                if (centering < attempts - 1) {
                    if (globe && zoom < 6) {
                        // A sphere's inverse projection near the horizon can jump to the
                        // other hemisphere. Solve the screen offset locally instead.
                        const center = transform.center;
                        const step = .01;
                        transform.center = new LngLat(center.lng + step, center.lat);
                        const dx = screenBounds();
                        transform.center = new LngLat(center.lng, center.lat + step);
                        const dy = screenBounds();
                        const a = (dx.x - x) / step, b = (dy.x - x) / step;
                        const c = (dx.y - y) / step, d = (dy.y - y) / step;
                        const determinant = a * d - b * c;
                        if (!Number.isFinite(determinant) || Math.abs(determinant) < 1e-8) {
                            fits = false;
                            break;
                        }
                        const longitude = Math.max(-20, Math.min(20, (-offsetX * d + b * offsetY) / determinant));
                        const latitude = Math.max(-20, Math.min(20, (c * offsetX - a * offsetY) / determinant));
                        transform.center = new LngLat(center.lng + longitude, Math.max(-85, Math.min(85, center.lat + latitude)));
                        continue;
                    }
                    const point = transform.centerPoint.clone();
                    point.x += offsetX;
                    point.y += offsetY;
                    try {
                        transform.center = transform.pointLocation(point);
                    }
                    catch {
                        fits = false;
                        break;
                    } // A trial point can be above the globe's horizon.
                }
            }
            if (fits) {
                best = { center: [transform.center.lng, transform.center.lat], zoom, bearing, pitch };
                lowerZoom = zoom;
            }
            else
                upperZoom = zoom;
            if (upperZoom - lowerZoom < .005)
                break;
            zoom = (lowerZoom + upperZoom) / 2;
        }
        return best;
    };
    return { getRouteAnimationCamera, getRouteAnimationCameraPlacement };
}
export const { getRouteAnimationCamera, getRouteAnimationCameraPlacement } = createRouteAnimationCameraTools();
export const ROUTE_ANIMATION_CAMERA_SCRIPT = `
const { getRouteAnimationCamera, getRouteAnimationCameraPlacement } = (${ANIMATION_CAMERA_SOURCE})();
`;
